/**
 * Define what is available at the root of this library
 */
import 'react-app-polyfill/stable';
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import { enableMapSet } from 'immer';

// "synchronously" import TS interface for TS compiler's sake.
import { IPropertyGridProps } from 'components/property-editors/PropertyGrid/PropertyGrid';
import { IEditorProps } from './components/Editors/Editor';
import { loadCustomComponents } from './loadCustomComponents';
import { IActionBarProps } from './components/ActionsBar/ActionsBar';
import { IEntityDetailsProps } from './components/EntityDetails/detailsTypes';
import { ENTITY_DETAILS_ID } from './components/EntityDetails/EntityDetails';
import { DESELECTALL_EVENT_PREFIX, ISampleTreeProps } from './components/UnitTests/SampleTree';
import { IUnitTestTreeProps } from './components/UnitTests/UnitTestTree';
import { IDataExplorerProps } from './components/Tree/DataDefTree/DataExplorer';
import { IStepsTreeProps } from 'domains/designers/FlowDesigner/StepsTree/StepsTree';
import { IDebuggerStartOptionsProps } from './components/Debugger/StartOptions';
import { IDebuggerInputTreeProps } from './components/Debugger/DebuggerInputTree';
import { IDesignerHeaderProps } from './domains/designers/portable-components/DesignerHeader';
import {
  CLOSE_EVENT_PREFIX,
  PortalSliderProps,
  SETONCLOSE_EVENT_PREFIX,
  SETTITLE_EVENT_PREFIX,
  TOGGLE_EVENT_PREFIX,
} from 'components/PortalSlider/PortalSlider';
import { ServiceFetcher, ServiceFetcherSettings } from '@decisions/server/ServiceFetcher';
import { getOrCreateDomElement } from 'components/layer/dialogHelper';
import { generateGuid } from 'utils/Guid';
import { createEvent } from 'utils/DomUtils';
import { IPortalHeaderProps, PortalHeader } from 'components/Portal/PortalHeader';
import { IFormTabEditorProps } from './domains/designers/FormDesigner/ToolBax/FormTabEditor/typings';
import { IFormHostProps, IFormSurfaceProps } from 'domains/designers/forms/formHostTypes';
import { IControlRenderOption } from './domains/designers/FormDesigner/FormDesignerControlRenderer';
import { IDateRangeFilterProps } from 'components/DateRangeFilter/DateRangeFilter';
import { IAppStoreProps } from 'components/AppStore/AppStore';
import { IDesignerPromptProps } from 'domains/designers/common/DesignerPrompt/DesignerPrompt';
import { ValidationRole } from 'components/inputs/Validation/ValidationError';
import { ValidationIssue } from '@decisions/server/DecisionsFramework';
import { getValidationSummary } from 'utils/ValidationUtils';
import { IEntityPropertiesProps } from 'components/EntityDetails/Panels/EntityProperties';
import { IModulesListProps } from 'components/AppStore/ModulesList';
import { IIntegrationDetailsProps } from 'components/IntegrationDetails/IntegrationModels';
import { IServiceIntegrationProps } from 'components/IntegrationDetails/ServiceIntegration/ServiceIntegration';
import { IMethodInformation } from 'components/IntegrationDetails/ServiceIntegration/MethodInformation';
import { IExternalFormIntegrationProps } from 'components/IntegrationDetails/ExternalFormIntegration/ExternalFormIntegration';
import { IDataTypeSerialization } from 'components/IntegrationDetails/DataTypeSerialization/DataTypeSerialization';
import { IProgressBarProps } from 'components/FileLoader/Upload/ProgressBar';

import { ISwitchProps } from './components/inputs/Switch/Switch';
import { ISimpleDropDownProps } from './components/inputs/SimpleDropDown/SimpleDropDown';
import { ISinglePropertyEditorProps } from 'components/property-editors/PropertyGrid/SinglePropertyEditor';
import { UPDATE_EVENT_PREFIX, TreeCommandType } from 'components/Tree/Legacy/useTreeModel';
import { ISaveDialogProps } from 'domains/designers/common/SaveDialog/SaveDialog';
import { renderServiceDisconnectedPopup } from './components/Portal/ServiceDisconnectedPopup';
import { IHorizontalFolderTimelinePartProps } from './components/PageControls/HorizontalFolderTimelinePart/HorizontalFolderTimelinePart';
import { IOSSCopyrightProps } from 'domains/portal/OSSCopyright/OSSCopyright';
import { RenderCallback, renderComponent } from 'utils/RenderingUtils';
import { IBackButtonProps } from 'components/PageControls/BackButton';
import { IAdvancedDataGridProps } from './domains/ReportViewer/AdvancedDataGrid';
import { IDrillDownReportViewerProps } from 'domains/ReportViewer/DrillDownReportViewer';
import { IDesignReportGridProps } from 'domains/ReportViewer/DesignReportGrid';
import { IImagePickerProps } from 'components/inputs/ImagePicker/ImagePicker';
import { IReportViewerProps } from 'domains/ReportViewer/reportTypes';

export { setUserCulture } from 'utils/UserCulture';
export { setPortalSetup } from 'utils/PortalSetup';
export { setIsMobile } from './config/PortalConfiguration';
export * from './utils'; // don't have time to really hook this up, just now.
export * from 'components/property-editors/MappingConstants';
export * from 'domains/designers/RuleTable';
export * from 'domains/designers/ExternalTruthTable';
export * from 'domains/designers/RuleDesigner';
export * from 'components/FavoriteIcon';
export { renderDependenciesBrowser } from 'domains/portal/DependenciesBrowser';
export * from 'domains/designers/DataDesigner';
export * from 'domains/designers/TruthTable';
export * from 'domains/designers/MatrixRule';
export { renderCreateRegistrationDialog } from 'components/CreateRegistration';
export { renderCreateFolderDialog } from 'components/CreateFolder';
export { renderCreateElement } from 'components/CreateElement';
export { renderDeleteElementDialog } from 'components/Dialogs/DeleteElementDialog';
export { renderCreateLinkElementDialog } from 'components/Dialogs/CreateLinkElement';
export { renderPickOrCreateDialog } from 'components/PickOrCreateRegistration';
export { DefaultNodeLoader } from './domains/portal/LeftPanel/FolderTree/NodeLoaders';
export * from './components/charts';
export { renderEitherOrInputDataDialog } from 'components/EitherOrDataDefinitions';
export { StepsTreeType } from 'domains/designers/FlowDesigner/StepsTree/StepsTree';
export { scrollToElement, TreeCommandType } from 'components/Tree/Legacy/useTreeModel';
export { renderMappingEditor } from 'domains/designers/FlowDesigner/index';
export { getTreeStore } from 'components/Tree/CommonTree/useTreeStore';
export { renderPropertyGridDialog } from 'components/Dialogs';
export { unMount } from 'utils/RenderingUtils';
export { renderButton } from 'components/buttons';
export * from 'domains/portal';

enableMapSet(); // need this once in the app to use immer to update maps: https://immerjs.github.io/immer/map-set/

const scriptLocation = process.env.PUBLIC_URL; // write compile time value to local variable
// This tells webpack to use virtualPath as the base from which to load lazy-loaded assets.
// If we ever add additional entry-points, they will need the same treatment.
if (!DEV_SERVER) {
  __webpack_public_path__ = `${virtualPath}${scriptLocation}/`;
}

/**
 * Handle for rendering the property grid into an HTML element.
 * @param container to house property grid component
 * @param props to render property grid with
 * @param callback when rendered
 */
export async function renderPropertyGrid(container: HTMLElement, props: IPropertyGridProps, callback?: RenderCallback) {
  // use webpack's "special" import syntax to asynchronously load React and PropertyGrid
  // in a separate chunks, and tell webpack what to name them all at once.

  // TODO use /* webpackPrefetch: true */ for React
  // if/when we get to webpack 4.6.0+
  // https://webpack.js.org/guides/code-splitting/#prefetchingpreloading-modules

  const { PropertyGrid } = await import(
    /* webpackChunkName: "dp-cmp-property-grid" */ './components/property-editors/PropertyGrid/PropertyGrid'
  );

  // register custom components before rendering:
  await loadCustomComponents(registerComponent);

  return renderComponent({
    container,
    component: <PropertyGrid {...props} />,
    callback,
    autoUnmount: false,
  });
}

export async function renderSinglePropertyEditor(
  container: HTMLElement,
  props: ISinglePropertyEditorProps,
  callback?: RenderCallback
) {
  const { SinglePropertyEditor } = await import(
    /* webpackChunkName: "dp-cmp-property-grid" */ './components/property-editors/PropertyGrid/SinglePropertyEditor'
  );

  // register custom components before rendering:
  await loadCustomComponents(registerComponent);

  return renderComponent({
    container,
    component: <SinglePropertyEditor {...props} />,
    callback,
    autoUnmount: false,
  });
}

export async function renderActionBar(container: HTMLElement, props: IActionBarProps, callback?: RenderCallback) {
  const { ActionsBar } = await import('./components/ActionsBar/ActionsBar');

  return renderComponent({
    container,
    component: <ActionsBar {...props} />,
    callback,
    autoUnmount: true,
  });
}

/**
 * Use to plug in new editor types at runtime, so backend changes an sync with them.
 *
 * Only other alternate would be allowing a pre-build hook that could run during a
 * webpack build while the system is running, similar to how we do it with .less files.
 *
 * Something similar to this
 * @param identifier used to lookup component type from data from server
 * @param component to render based on data
 */
export async function registerComponent(
  identifier: string,
  component: React.FunctionComponent<IEditorProps> | React.ComponentClass<IEditorProps>
) {
  // EditorMap is used synchronously inside the property grid chunk, so we'll leave
  // it there for now, instead of having duplicate chunks or adding the commons-chunk
  // plugin to the configuration for now.
  const { EditorMap } = await import(/* webpackChunkName: "dp-cmp-property-grid" */ './components/Editors/EditorMap');
  // add component to map. This could be decoupled from editors in the future.
  EditorMap[identifier] = component;
}

export async function renderEntityDetailsSlider(
  sliderOptions: PortalSliderProps,
  entityDetails: IEntityDetailsProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { PortalSlider } = await import('./components/PortalSlider/PortalSlider');
  const { EntityDetails } = await import('./components/EntityDetails/EntityDetails');

  const oldClose = sliderOptions.onClose;

  sliderOptions.openPortalSlider = true;
  sliderOptions.sliderId = ENTITY_DETAILS_ID;
  sliderOptions.applyTabbing = false;
  sliderOptions.onClose = () => {
    root.unmount();
    oldClose && oldClose();
  };

  const root = await renderComponent({
    container,
    component: (
      <PortalSlider {...sliderOptions}>
        <EntityDetails {...entityDetails} />
      </PortalSlider>
    ),
    callback,
    autoUnmount: false,
  });

  return root;
}

export async function renderPortalSlider(
  sliderOptions: PortalSliderProps,
  onUnmount?: (element: HTMLElement) => void,
  callback?: RenderCallback
) {
  const { PortalSlider } = await import('./components/PortalSlider/PortalSlider');

  const oldClose = sliderOptions.onClose;
  const id = sliderOptions.sliderId || generateGuid();
  const container = getOrCreateDomElement({ id });

  sliderOptions.onClose = () => {
    onUnmount && onUnmount(container);
    root.unmount();
    container?.parentElement?.removeChild(container);
    oldClose && oldClose();
  };

  const root = await renderComponent({
    container,
    component: <PortalSlider {...sliderOptions} />,
    callback,
    autoUnmount: false,
  });
  sliderOptions.sliderId = id;

  return root;
}

export async function closePortalSlider(id: string) {
  window.dispatchEvent(createEvent(`${CLOSE_EVENT_PREFIX}${id}`));
}

export async function togglePortalSlider(id: string, show: boolean) {
  window.dispatchEvent(createEvent(`${TOGGLE_EVENT_PREFIX}${id}`, { show: show }));
}

export async function setPortalSliderTitle(id: string, title: string) {
  window.dispatchEvent(createEvent(`${SETTITLE_EVENT_PREFIX}${id}`, { title: title }));
}

export async function setPortalSliderOnClose(id: string, onClose: () => void) {
  window.dispatchEvent(createEvent(`${SETONCLOSE_EVENT_PREFIX}${id}`, { onClose: onClose }));
}

export async function renderEntityDetails(
  options: IEntityDetailsProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { EntityDetails } = await import('./components/EntityDetails/EntityDetails');

  return renderComponent({
    container,
    component: <EntityDetails {...options} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderFolderActionsBar(
  container: HTMLElement,
  options: IActionBarProps,
  callback?: RenderCallback
) {
  const { FolderActionsBar } = await import('./components/ActionsBar/FolderActionsBar');

  return renderComponent({
    container,
    component: <FolderActionsBar {...options} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderPortalHeader(
  container: HTMLElement,
  options: IPortalHeaderProps,
  callback?: RenderCallback
) {
  return renderComponent({
    container,
    component: <PortalHeader {...options} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderReportViewer(props: IReportViewerProps, container: HTMLElement, callback?: RenderCallback) {
  const { ReportViewerContainer: ReportViewer } = await import('./domains/ReportViewer/ReportViewerContainer');

  return renderComponent({
    container,
    component: <ReportViewer {...props} />,
    callback,
    // TODO: test auto unmount
    autoUnmount: true,
  });
}

export async function renderDrillDownReportViewer(
  props: IDrillDownReportViewerProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { DrillDownReportViewer } = await import('./domains/ReportViewer/DrillDownReportViewer');

  return renderComponent({
    container,
    component: <DrillDownReportViewer {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderDesignReportGrid(
  props: IDesignReportGridProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { DesignReportGrid } = await import('./domains/ReportViewer/DesignReportGrid');

  return renderComponent({
    container,
    component: <DesignReportGrid {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderAdvancedDataGridReportViewer(
  props: IAdvancedDataGridProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { AdvancedDataGridReportViewer } = await import('./domains/ReportViewer/AdvancedDataGrid');

  return renderComponent({
    container,
    component: <AdvancedDataGridReportViewer {...props} />,
    callback,
    autoUnmount: true,
  });
}

export function setupServiceFetcher(settings: ServiceFetcherSettings) {
  ServiceFetcher.setup(settings);
}

export async function renderFormDesignerTabEditor(
  props: IFormTabEditorProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { FormTabEditor } = await import('./domains/designers/FormDesigner/ToolBax/FormTabEditor/FormTabEditor');

  return renderComponent({
    container,
    component: <FormTabEditor {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderFormHost(props: IFormHostProps, container: HTMLElement, callback?: RenderCallback) {
  const { FormHostContainer } = await import('./domains/designers/forms/FormHostContainer');

  return renderComponent({
    container,
    component: <FormHostContainer {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderFormControlInDesigner(
  props: IControlRenderOption,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { FormDesignerControlRenderer } = await import('./domains/designers/FormDesigner/FormDesignerControlRenderer');

  return renderComponent({
    container,
    component: <FormDesignerControlRenderer {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderRootContainerInFormDesigner(
  props: IFormSurfaceProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { RootContainer } = await import('./domains/designers/FormDesigner/RootContainer');

  return renderComponent({
    container,
    component: <RootContainer {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderSampleTree(props: ISampleTreeProps, container: HTMLElement, callback?: RenderCallback) {
  const { SampleTree } = await import('./components/UnitTests/SampleTree');

  return renderComponent({
    container,
    component: <SampleTree {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderUnitTestTree(props: IUnitTestTreeProps, container: HTMLElement, callback?: RenderCallback) {
  const { UnitTestTree } = await import('./components/UnitTests/UnitTestTree');

  return renderComponent({
    container,
    component: <UnitTestTree {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function treeDeselectAll(treeId: string) {
  window.dispatchEvent(createEvent(`${DESELECTALL_EVENT_PREFIX}${treeId}`));
}

export async function treeUpdate(treeId: string, command: TreeCommandType, id: string) {
  window.dispatchEvent(createEvent(`${UPDATE_EVENT_PREFIX}${treeId}`, { command: command, id: id }));
}

export async function renderDataExplorer(props: IDataExplorerProps, container: HTMLElement, callback?: RenderCallback) {
  const { DataExplorer } = await import('./components/Tree/DataDefTree/DataExplorer');

  return renderComponent({
    container,
    component: <DataExplorer {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderStepsTree(props: IStepsTreeProps, container: HTMLElement, callback?: RenderCallback) {
  const { StepsTree } = await import('./domains/designers/FlowDesigner/StepsTree/StepsTree');

  return renderComponent({
    container,
    component: <StepsTree {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderDebuggerStartOptions(
  props: IDebuggerStartOptionsProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { DebuggerStartOptions } = await import('./components/Debugger/StartOptions');

  return renderComponent({
    container,
    component: <DebuggerStartOptions {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderDebuggerInputTree(
  props: IDebuggerInputTreeProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { DebuggerInputTree } = await import('./components/Debugger/DebuggerInputTree');

  return renderComponent({
    container,
    component: <DebuggerInputTree {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderDesignerHeader(
  props: IDesignerHeaderProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { DesignerHeader } = await import('./domains/designers/portable-components/DesignerHeader');

  return renderComponent({
    container,
    component: <DesignerHeader {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderDateRangeFilter(
  props: IDateRangeFilterProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { DateRangeFilter } = await import('./components/DateRangeFilter/DateRangeFilter');

  return renderComponent({
    container,
    component: <DateRangeFilter {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderAppStore(props: IAppStoreProps, container: HTMLElement, callback?: RenderCallback) {
  const { AppStore } = await import('./components/AppStore/AppStore');

  return renderComponent({
    container,
    component: <AppStore {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderDesignerPrompt(
  props: IDesignerPromptProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { DesignerPrompt } = await import('domains/designers/common');

  //TODO: need to add ability to update
  return renderComponent({
    container,
    component: <DesignerPrompt {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderValidationError(
  issues: ValidationIssue[],
  className: string,
  role: ValidationRole,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { ValidationError } = await import('components/inputs/Validation/ValidationError');

  return renderComponent({
    container,
    component: <ValidationError {...getValidationSummary(issues)} role={role} className={className} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderEntityProperties(
  props: IEntityPropertiesProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { EntityProperties } = await import('components/EntityDetails/Panels/EntityProperties');

  return renderComponent({
    container,
    component: <EntityProperties {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderModulesList(props: IModulesListProps, container: HTMLElement, callback?: RenderCallback) {
  const { ModulesList } = await import('components/AppStore/ModulesList');

  return renderComponent({
    container,
    component: <ModulesList {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderProgressBar(props: IProgressBarProps, container: HTMLElement, callback?: RenderCallback) {
  const { ProgressBar } = await import('components/FileLoader/Upload/ProgressBar');

  return renderComponent({
    container,
    callback,
    component: <ProgressBar {...props} />,
    autoUnmount: true,
  });
}

export async function renderSwitch(props: ISwitchProps, container: HTMLElement, callback?: RenderCallback) {
  const { Switch } = await import('components/inputs/Switch/Switch');

  return renderComponent({
    container,
    callback,
    component: <Switch {...props} />,
    autoUnmount: true,
  });
}

export async function renderSimpleDropDown(
  props: ISimpleDropDownProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { SimpleDropDown } = await import('components/inputs/SimpleDropDown/SimpleDropDown');

  return renderComponent({
    container,
    callback,
    component: <SimpleDropDown {...props} />,
    autoUnmount: true,
  });
}

export async function renderViewIntegrationDetailsPage(
  props: IIntegrationDetailsProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { IntegrationDetails } = await import('components/IntegrationDetails/IntegrationDetails');

  return renderComponent({
    container,
    callback,
    component: <IntegrationDetails {...props} />,
    autoUnmount: true,
  });
}

export async function renderServiceIntegration(
  props: IServiceIntegrationProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { ServiceIntegration } = await import('components/IntegrationDetails/ServiceIntegration/ServiceIntegration');

  return renderComponent({
    container,
    callback,
    component: <ServiceIntegration {...props} />,
    autoUnmount: true,
  });
}

export async function renderMethodInformation(
  props: IMethodInformation,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { MethodInformation } = await import('components/IntegrationDetails/ServiceIntegration/MethodInformation');
  callback && requestIdleCallback(() => callback);

  return renderComponent({
    container,
    callback,
    component: <MethodInformation {...props} />,
    autoUnmount: true,
  });
}

export async function renderExternalFormIntegration(
  props: IExternalFormIntegrationProps,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { ExternalFormIntegration } = await import(
    'components/IntegrationDetails/ExternalFormIntegration/ExternalFormIntegration'
  );

  return renderComponent({
    container,
    callback,
    component: <ExternalFormIntegration {...props} />,
    autoUnmount: true,
  });
}

export async function renderDataTypeSerialization(
  props: IDataTypeSerialization,
  container: HTMLElement,
  callback?: RenderCallback
) {
  const { DataTypeSerialization } = await import(
    'components/IntegrationDetails/DataTypeSerialization/DataTypeSerialization'
  );

  return renderComponent({
    container,
    callback,
    component: <DataTypeSerialization {...props} />,
    autoUnmount: true,
  });
}

export async function renderSaveDialog(props: ISaveDialogProps, container: HTMLElement, callback?: RenderCallback) {
  const { SaveDialog } = await import('domains/designers/common/SaveDialog/SaveDialog');

  return renderComponent({
    container,
    callback,
    component: <SaveDialog {...props} />,
    autoUnmount: false,
  });
}

export async function renderOssCopyright(props: IOSSCopyrightProps, container: HTMLElement, callback?: RenderCallback) {
  const { OSSCopyright } = await import('domains/portal/OSSCopyright/OSSCopyright');

  return renderComponent({
    container,
    component: <OSSCopyright {...props} />,
    callback,
    autoUnmount: true,
  });
}

export async function renderServiceDisconnected() {
  renderServiceDisconnectedPopup();
}

export async function renderHorizontalFolderTimelinePart(
  props: IHorizontalFolderTimelinePartProps,
  container: HTMLElement,
  callback: RenderCallback
) {
  const { HorizontalFolderTimelinePart } = await import(
    './components/PageControls/HorizontalFolderTimelinePart/HorizontalFolderTimelinePart'
  );

  return renderComponent({
    container,
    component: <HorizontalFolderTimelinePart {...props} />,
    autoUnmount: true,
    callback,
  });
}

export async function renderBackButton(props: IBackButtonProps, container: HTMLElement, callback: RenderCallback) {
  const { BackButton } = await import('components/PageControls/BackButton');

  return renderComponent({
    container,
    component: <BackButton {...props} />,
    autoUnmount: true,
    callback,
  });
}

export async function renderImagePicker(props: IImagePickerProps, container: HTMLElement) {
  const { ImagePicker } = await import('components/inputs/ImagePicker/ImagePicker');

  return renderComponent({
    container,
    component: <ImagePicker {...props} />,
    autoUnmount: true,
  });
}
